import { useAuthenticatedWebsocketCore } from "@fraction/shared-frontend";
import { useEffect } from "react";
import { useAuthUser } from "src/auth/useAuth";
import config from "src/config";

/**
 * Web wrapper over the shared `useAuthenticatedWebsocketCore`: injects the web
 * websocket URL + auth (with borrower-impersonation overrides) and adds the
 * web-only tab-visibility re-register.
 *
 * Message delivery is callback-first — prefer `onMessage` over reading
 * `lastMessage` via a `useEffect`.
 *
 * For borrower-mode chat we have no JWT; pass `tokenOverride` (the
 * BorrowerSession id) and `subscriberIdOverride` (the synthetic `session:<id>`
 * key the backend registers under). When both are set, the hook uses them
 * instead of `useAuthUser()`.
 *
 * Pass `sessionId` to subscribe to a per-session topic (`session:<id>`) —
 * borrowers and watching employees both use this to receive pushes for one
 * specific AgentSession. The server authorizes the subscribe based on the
 * (token, sessionId) pair.
 */
export function useWebsocketAuthenticated<T = any>({
  routeKey,
  enabled = true,
  tokenOverride,
  subscriberIdOverride,
  sessionId,
  onMessage,
  onOpen,
}: {
  routeKey?: string;
  enabled?: boolean;
  tokenOverride?: string | null;
  subscriberIdOverride?: string | null;
  sessionId?: string | null;
  onMessage?: (msg: T) => void;
  /** Fires every time the underlying socket opens — both the initial connect
   *  and any subsequent reconnect after a drop. Use this to refetch server
   *  state after a connection gap. */
  onOpen?: () => void;
}) {
  const auth = useAuthUser();
  const userId = subscriberIdOverride ?? auth.user?.id;
  const token = tokenOverride ?? auth.token;

  const { lastMessage, readyState, register } = useAuthenticatedWebsocketCore<T>({
    websocketUrl: config.websocketURL,
    token,
    userId,
    routeKey,
    enabled,
    sessionId,
    onMessage,
    onOpen,
  });

  // Re-register when tab becomes visible (connection may have been dropped while backgrounded)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        register();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [register]);

  return { lastMessage, readyState };
}
